import { useEffect, useState } from "react";
import api, { fmt, fmtCents } from "@/lib/api";
import { toast } from "sonner";
import { Target, Plus, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATS = ["Restaurants","Groceries","Shopping","Entertainment","Transportation","Fuel","Travel","Subscriptions","Personal Care","Kids & Family","Other"];

export default function CategoryBudgets() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [cat, setCat] = useState("Restaurants");
  const [cap, setCap] = useState(500);
  const [suggestion, setSuggestion] = useState(null);

  const load = async () => {
    const { data } = await api.get("/budgets");
    setItems(data);
    data.forEach((b) => {
      if (b.pct >= 80 && b.pct < 100 && !sessionStorage.getItem(`budget-alert-${b.category}`)) {
        toast.warning(`${b.category}: ${b.pct.toFixed(0)}% of budget used`, { description: `${fmtCents(b.spent)} of ${fmtCents(b.monthly_cap)} this month` });
        sessionStorage.setItem(`budget-alert-${b.category}`, "1");
      }
      if (b.pct >= 100 && !sessionStorage.getItem(`budget-over-${b.category}`)) {
        toast.error(`${b.category} over budget`, { description: `${fmtCents(b.spent)} spent · cap is ${fmtCents(b.monthly_cap)}` });
        sessionStorage.setItem(`budget-over-${b.category}`, "1");
      }
    });
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get(`/budgets/suggest?category=${encodeURIComponent(cat)}`);
        setSuggestion(data);
        if (data.suggested_cap > 0) setCap(data.suggested_cap);
      } catch { setSuggestion(null); }
    })();
  }, [cat, open]);

  const save = async () => {
    try {
      await api.post("/budgets", { category: cat, monthly_cap: Number(cap) });
      toast.success("Budget saved");
      setOpen(false);
      load();
    } catch { toast.error("Failed"); }
  };
  const del = async (c) => {
    await api.delete(`/budgets/${encodeURIComponent(c)}`);
    load();
  };

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Category budgets</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="add-budget-btn" className="text-xs font-semibold text-slate-600 hover:text-slate-900 flex items-center gap-1 transition-colors"><Plus size={12} /> Set a cap</button>
          </DialogTrigger>
          <DialogContent className="max-w-sm rounded-3xl">
            <DialogHeader><DialogTitle className="font-manrope">Set monthly cap</DialogTitle></DialogHeader>
            <div className="mt-2 space-y-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Category</label>
                <Select value={cat} onValueChange={setCat}>
                  <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              {suggestion && suggestion.suggested_cap > 0 && (
                <button
                  data-testid="use-suggested-cap-btn"
                  type="button"
                  onClick={() => setCap(suggestion.suggested_cap)}
                  className="w-full text-left rounded-2xl bg-emerald-50 border border-emerald-200 hover:border-emerald-400 p-3 flex items-start gap-2.5 transition-colors"
                >
                  <Sparkles size={16} className="text-emerald-600 shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-bold">Suggested cap</div>
                    <div className="font-manrope font-extrabold text-slate-900 tabular-nums text-lg">{fmtCents(suggestion.suggested_cap)}<span className="text-slate-400 text-xs font-medium">/mo</span></div>
                    <div className="text-[11px] text-slate-600 leading-relaxed mt-0.5">{suggestion.reasoning}</div>
                  </div>
                </button>
              )}

              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Monthly cap</label>
                <input data-testid="budget-cap-input" type="number" min="0" value={cap} onChange={(e) => setCap(e.target.value)} className="mt-2 w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-slate-900" />
              </div>
              <button data-testid="budget-save-btn" onClick={save} className="w-full bg-slate-900 text-white rounded-full py-3 font-semibold hover:bg-slate-800 transition-colors">Save cap</button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <div className="mt-3 text-slate-400 text-xs flex items-center gap-2"><Target size={12} /> Set a cap for any category you want to keep in check.</div>
      ) : (
        <div className="mt-3 space-y-2">
          {items.map((b) => {
            const over = b.pct >= 100;
            const warn = b.pct >= 80 && !over;
            const barColor = over ? "bg-red-500" : warn ? "bg-amber-500" : "bg-emerald-500";
            return (
              <div key={b.category} className="rounded-xl border border-slate-200 p-3" data-testid={`budget-${b.category}`}>
                <div className="flex items-baseline justify-between">
                  <div className="text-sm font-medium text-slate-900">{b.category}</div>
                  <div className="text-xs tabular-nums text-slate-600">{fmtCents(b.spent)} <span className="text-slate-400">/ {fmtCents(b.monthly_cap)}</span></div>
                </div>
                <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className={`h-full ${barColor} transition-all duration-500`} style={{ width: `${Math.min(100, b.pct)}%` }} />
                </div>
                <div className="mt-1.5 flex justify-between text-[11px]">
                  <span className={over ? "text-red-600 font-semibold" : warn ? "text-amber-700 font-semibold" : "text-slate-500"}>
                    {over ? `Over by ${fmt(b.spent - b.monthly_cap)}` : warn ? `${b.pct.toFixed(0)}% used` : `${fmt(b.remaining)} left`}
                  </span>
                  <button onClick={() => del(b.category)} className="text-slate-400 hover:text-slate-700 transition-colors">Remove</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
